import { useEffect } from 'react';

import { useSyncRef } from '../syncRef';

export interface KeyPressV2Params {
  key: string;

  /**
   * Receive the whole event to handle/parse
   */
  onEvent?: (e: KeyboardEvent) => void;

  /**
   * A simple action to perform given the conditions met
   *
   * If you want more control over the event, use `onEvent`
   */
  action?: () => void;

  /**
   * Prevents the default browser action, if there's any
   *
   * Defaults to `TRUE`
   */
  preventDefault?: boolean;

  /**
   * Stops the event from reaching other handlers
   *
   * Useful if you have a combo inside a modal that is also used more globally
   *
   * Defaults to `FALSE`
   */
  stopPropagation?: boolean;

  /**
   * Conditionally block the whole interaction
   *
   * If false, the press will act as the hook was not called
   */
  isEnabled?: boolean;
}

export function useKeyPressV2({ key, ...params }: KeyPressV2Params) {
  const cache = useSyncRef({
    ...params,
  });

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      const {
        action,
        onEvent,
        stopPropagation,
        isEnabled = true,
        preventDefault = true,
      } = cache.current;

      if (!isEnabled) return;

      const isMatch = key.toUpperCase() === event.key.toUpperCase();

      if (!isMatch) return;

      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      onEvent?.(event);
      action?.();
    };

    document.addEventListener('keydown', keyHandler);

    return () => document.removeEventListener('keydown', keyHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
